import { Group, ActionIcon, Text, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { MaterialIcon } from '../common/MaterialIcon';
import { useCurrentDate, useCurrentDateActions } from '../../hooks/useCurrentDate';
import { useFieldEventStore } from '../../stores/fieldEventStore';
import { isDebugFieldStateUIEnabled } from '../../utils/debugUtils';

/**
 * Date を YYYY-MM-DD 形式の文字列に変換
 */
function dateToString(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * YYYY-MM-DD 形式の文字列を Date に変換
 */
function stringToDate(str: string | null): Date | null {
  if (!str) return null;
  const [year, month, day] = str.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * デバッグ用「現在の日付」変更パネル
 *
 * 環境変数 VITE_DEBUG_FIELD_STATE_UI=true の場合のみ表示
 * ヘッダーに配置され、現在の日付を変更してUI表示を検証できる
 *
 * 日付変更時はバックエンド API に asOf パラメータ付きで再取得し、
 * サーバー側でステートを再計算する。
 *
 * 既知の制約: 日付変更後に新しく読み込まれる圃場（画面遷移やフィルタ切替で
 * 初めて取得される圃場）には asOf が適用されず、サーバーの現在日付で取得される。
 * fieldEventStore から currentDateStore への依存を避けるため、現時点では未対応。
 */
export function DebugCurrentDatePanel() {
  const currentDate = useCurrentDate();
  const { setOverrideDate, resetToRealToday } = useCurrentDateActions();
  const refetchAllWithDate = useFieldEventStore((state) => state.refetchAllWithDate);

  // デバッグモードでない場合は何も表示しない
  if (!isDebugFieldStateUIEnabled()) {
    return null;
  }

  // Mantine DateInput は YYYY-MM-DD 形式の文字列を使用
  const currentDateStr = dateToString(currentDate);

  const handleDateChange = (value: string | null) => {
    const date = stringToDate(value);
    setOverrideDate(date);
    // デバッグ日付変更に伴い、バックエンドから asOf 付きで再取得
    // date が null（クリア）の場合はサーバーの現在日付で再取得
    setTimeout(() => refetchAllWithDate(date ?? undefined), 0);
  };

  const handleResetToToday = () => {
    resetToRealToday();
    // 今日にリセット → asOf なしで再取得（サーバーの現在日付で計算）
    setTimeout(() => refetchAllWithDate(), 0);
  };

  return (
    <Group
      gap="xs"
      style={{
        backgroundColor: '#fef3cd', // デバッグUI: 黄色系の背景
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid #ffc107', // デバッグUI: 黄色系のボーダー
      }}
    >
      <Text size="xs" c="orange.8" fw={500}>
        🔧 現在の日付:
      </Text>
      <DateInput
        value={currentDateStr}
        onChange={handleDateChange}
        valueFormat="YYYY/MM/DD"
        size="xs"
        styles={{
          input: {
            width: '110px',
            minHeight: '24px',
            height: '24px',
            padding: '0 8px',
          },
        }}
      />
      <Tooltip label="今日に戻す" position="bottom">
        <ActionIcon
          variant="subtle"
          color="orange.8"
          size="xs"
          onClick={handleResetToToday}
          aria-label="今日に戻す"
        >
          <MaterialIcon icon="today" size="sm" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
